import type { DataTableSortState } from '@atrvd/saas-ui'
import type { SageBankAccountRow } from '@/lib/db-tenant'
import type { Facility } from '@/types/banking'
import { resolveFacilityWinnersByLocationKey, type CheckAggregate } from '@/lib/sage-utils'
import { matchesAccountSearch } from '@/lib/account-search'
import { compareText } from '@/components/manual-banks/manualBanks.utils'
import { signed } from '@/components/manual-banks/manualBankSort'

/** A Sage bank account with the values the Settings table shows, filters and sorts on. */
export interface SageBankAccountTableRow {
  account: SageBankAccountRow
  /** The facility that owns the account's Sage location, or null when unmapped. */
  facilityId: string | null
  facilityName: string | null
  /** Whether that facility is active, or null when unmapped. */
  facilityActive: boolean | null
  checks: CheckAggregate
}

/** Why an account can never be counted in the Intacct numbers, whatever its stored opt-in. */
export type SageIncludeLock = 'missing' | 'inactive'

/**
 * Why an account is never counted in Cash Position regardless of its stored
 * `include_in_cash_position`, or null when that flag decides. Must agree with
 * `applyIntacctScope` in `lib/db-tenant.ts` and the SQL `intacct` CTE (both
 * count only `status = 'active' AND NOT missing_in_sage`). Missing wins over
 * inactive, the same precedence as the row badges.
 */
export function sageIncludeLock(
  account: Pick<SageBankAccountRow, 'status' | 'missing_in_sage'>,
): SageIncludeLock | null {
  if (account.missing_in_sage) return 'missing'
  if (account.status !== 'active') return 'inactive'
  return null
}

/** Whether the account is actually counted: opted in and not locked out by Sage. */
export function isSageAccountIncluded(
  account: Pick<SageBankAccountRow, 'status' | 'missing_in_sage' | 'include_in_cash_position'>,
): boolean {
  return account.include_in_cash_position && sageIncludeLock(account) === null
}

const NO_CHECKS: CheckAggregate = { total: 0, outstanding: 0, outstandingAmount: 0 }

/**
 * Resolve each account's facility (case-insensitive, same winner rule as the
 * Cash Position engine) and its check counts.
 *
 * @param accounts - Every Sage bank account, inactive and missing ones included
 * @param facilities - Every facility, inactive ones included, so a name always resolves and a
 *   location shared with an active facility resolves to it, which makes `facilityActive` right
 * @param checksByEntity - Check aggregates keyed by `financial_entity_id`
 */
export function toSageBankAccountTableRows(
  accounts: readonly SageBankAccountRow[],
  facilities: readonly Facility[],
  checksByEntity: ReadonlyMap<string, CheckAggregate>,
): SageBankAccountTableRow[] {
  const winners = resolveFacilityWinnersByLocationKey(facilities)
  return accounts.map((account) => {
    const winner = account.location_id ? winners.get(account.location_id.toUpperCase()) : undefined
    return {
      account,
      facilityId: winner?.id ?? null,
      facilityName: winner?.name ?? null,
      facilityActive: winner?.isActive ?? null,
      checks: checksByEntity.get(account.financial_entity_id) ?? NO_CHECKS,
    }
  })
}

/**
 * Drop the accounts whose winning facility is inactive; unmapped accounts stay
 * (ADR 0016: inactive is hidden by default, unassigned is not). A location
 * shared by an active and an inactive facility resolves to the active one, so
 * its accounts stay too.
 */
export function withoutInactiveFacilityRows(
  rows: readonly SageBankAccountTableRow[],
): SageBankAccountTableRow[] {
  return rows.filter((row) => row.facilityActive !== false)
}

/** The table's client-side filters. */
export interface SageBankAccountFilters {
  search: string
  /** Selected facility ids; empty = all facilities. */
  facilityIds: string[]
}

/** Whether any filter narrows the table (drives the filtered-empty copy and "Clear filters"). */
export function hasActiveFilters(filters: SageBankAccountFilters): boolean {
  return filters.search.trim() !== '' || filters.facilityIds.length > 0
}

/**
 * Apply the search and facility filters. Search matches the account name or
 * the account number (`matchesAccountSearch`, the same rule as the other
 * account lists), not the Sage code; filters combine with AND.
 */
export function filterSageBankAccountRows(
  rows: readonly SageBankAccountTableRow[],
  filters: SageBankAccountFilters,
): SageBankAccountTableRow[] {
  return rows.filter((row) => {
    const { name, bank_account_no } = row.account
    if (!matchesAccountSearch({ name, account_number: bank_account_no }, filters.search)) {
      return false
    }
    if (filters.facilityIds.length > 0) {
      if (row.facilityId === null || !filters.facilityIds.includes(row.facilityId)) return false
    }
    return true
  })
}

/**
 * Facility filter options: the facilities that own at least one listed
 * account, by name. Offering every facility would list choices that can only
 * empty the table.
 */
export function facilityFilterOptions(
  rows: readonly SageBankAccountTableRow[],
): Array<{ value: string; label: string }> {
  const byId = new Map<string, string>()
  for (const row of rows) {
    if (row.facilityId !== null && row.facilityName !== null) byId.set(row.facilityId, row.facilityName)
  }
  return [...byId]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => compareText(a.label, b.label))
}

/** Column keys of the table, which are also its sort keys. */
export const SAGE_BANK_ACCOUNT_COLUMNS = {
  name: 'name',
  accountNumber: 'bank_account_no',
  facility: 'facility',
  checks: 'checks',
  outstanding: 'outstanding',
  include: 'include_in_cash_position',
} as const

type Comparator = (a: SageBankAccountTableRow, b: SageBankAccountTableRow) => number

const COMPARATORS: Record<string, Comparator> = {
  [SAGE_BANK_ACCOUNT_COLUMNS.name]: (a, b) => compareText(a.account.name, b.account.name),
  [SAGE_BANK_ACCOUNT_COLUMNS.accountNumber]: (a, b) =>
    compareText(a.account.bank_account_no ?? '', b.account.bank_account_no ?? ''),
  [SAGE_BANK_ACCOUNT_COLUMNS.facility]: (a, b) =>
    compareText(a.facilityName ?? '', b.facilityName ?? ''),
  [SAGE_BANK_ACCOUNT_COLUMNS.checks]: (a, b) => a.checks.total - b.checks.total,
  [SAGE_BANK_ACCOUNT_COLUMNS.outstanding]: (a, b) =>
    a.checks.outstanding - b.checks.outstanding ||
    a.checks.outstandingAmount - b.checks.outstandingAmount,
  // The displayed state, not the stored flag: a locked account reads "No", and
  // sorting it among the "Yes" rows would make the column look unsorted.
  [SAGE_BANK_ACCOUNT_COLUMNS.include]: (a, b) =>
    Number(isSageAccountIncluded(a.account)) - Number(isSageAccountIncluded(b.account)),
}

/**
 * Sort the whole filtered set before it is paged — DataTable would only sort
 * the slice it is handed. Ties fall back to name, then Sage code, ascending in
 * both directions, so equal rows never swap places between renders or pages.
 *
 * @param rows - The filtered rows
 * @param sort - The active sort; empty, cleared or an unknown column keeps the input order
 */
export function sortSageBankAccountRows(
  rows: readonly SageBankAccountTableRow[],
  sort: readonly DataTableSortState[],
): SageBankAccountTableRow[] {
  const active = sort[0]
  const direction = active?.direction
  const compare = active ? COMPARATORS[active.columnKey] : undefined
  if (!direction || !compare) return [...rows]
  return [...rows].sort(
    (a, b) =>
      signed(direction, compare(a, b)) ||
      compareText(a.account.name, b.account.name) ||
      compareText(a.account.financial_entity_id, b.account.financial_entity_id),
  )
}
